export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      title: 'Describe Your Project',
      description: 'Tell us about your home improvement needs, project scope, timeline, and budget. Whether it\'s a simple repair or major renovation, provide as much detail as possible to help contractors understand your requirements.',
      icon: 'ri-edit-box-line'
    },
    {
      number: '02',
      title: 'Browse Qualified Contractors',
      description: 'Search our directory by service type, neighborhood, or specialty. Review contractor profiles, read verified customer reviews, view completed projects, and compare qualifications to find the perfect match for your needs.',
      icon: 'ri-search-line'
    },
    {
      number: '03',
      title: 'Request Quotes',
      description: 'Contact multiple contractors to request detailed estimates. Compare pricing, timelines, materials, and approaches. Ask questions about their experience with similar projects and verify their availability for your preferred schedule.',
      icon: 'ri-file-list-line'
    },
    {
      number: '04',
      title: 'Hire with Confidence',
      description: 'Select the contractor that best fits your needs and budget. Review and sign contracts carefully, establish clear communication channels, and set expectations for project milestones. Our vetted professionals ensure quality results.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  return (
    <section className="py-20 px-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            How It Works: Finding Your Perfect Contractor
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Our streamlined process makes it easy to connect with qualified contractors in El Paso. From initial search to project completion, we guide you through every step to ensure a successful home improvement experience.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="bg-white rounded-xl p-6 h-full hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-5xl font-bold text-teal-600/20">
                    {step.number}
                  </div>
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-lg flex-shrink-0">
                    <i className={`${step.icon} text-2xl text-white`}></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <i className="ri-arrow-right-line text-2xl text-teal-600"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Tips for a Successful Contractor Relationship
          </h3>
          <div className="grid md:grid-cols-2 gap-8 text-sm text-gray-700 leading-relaxed">
            <div className="space-y-4">
              <p>
                <strong className="text-gray-900">Clear Communication is Key:</strong> Establish expectations upfront regarding communication frequency, preferred contact methods, and response times. Regular updates prevent misunderstandings and keep projects on track. Don't hesitate to ask questions or request clarification on any aspect of the work.
              </p>
              <p>
                <strong className="text-gray-900">Document Everything:</strong> Keep written records of all agreements, change orders, payment schedules, and warranties. Take photos before, during, and after the project. Documentation protects both you and the contractor and provides valuable reference for future maintenance or warranty claims.
              </p>
              <p>
                <strong className="text-gray-900">Understand the Contract:</strong> Read contracts thoroughly before signing. Ensure they include detailed scope of work, materials specifications, payment schedule, timeline, warranty information, and procedures for handling changes or disputes. Never sign a blank or incomplete contract.
              </p>
            </div>
            <div className="space-y-4">
              <p>
                <strong className="text-gray-900">Payment Best Practices:</strong> Never pay the full amount upfront. Standard practice involves a deposit (typically 10-30%), progress payments tied to completed milestones, and final payment upon satisfactory completion and inspection. Use checks or credit cards for payment documentation.
              </p>
              <p>
                <strong className="text-gray-900">Verify Permits and Inspections:</strong> Ensure your contractor obtains all necessary permits before work begins. Permits protect you by ensuring work meets code requirements and maintains your home's value. Verify that required inspections are scheduled and passed at appropriate project stages.
              </p>
              <p>
                <strong className="text-gray-900">Maintain Realistic Expectations:</strong> Understand that unexpected issues can arise, especially in older homes. Weather, material availability, and permit delays can impact timelines. Work with your contractor to address challenges professionally and adjust plans as needed while maintaining quality standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
